import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Connection,
    IsValidConnection,
    ReactFlowProvider,
    XYPosition,
} from "@xyflow/react";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useStartAddEntity } from "@components/addEntity/useStartAddEntity";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useEntities } from "@hooks/useEntities";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useOverlays } from "contexts/OverlayContext";
import {
    useConsolidationGroups,
    useConsolidationRelationships,
} from "@api/consolidation.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import {
    ConsolidationRelationshipResponse,
    UpdateConsolidationRelationshipBody,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { OwnershipCanvas } from "./canvas/OwnershipCanvas";
import { OwnershipCommandBar } from "./OwnershipCommandBar";
import { OwnershipSidePanel } from "./panels/OwnershipSidePanel";
import {
    RelationshipFormPane,
    RelationshipFormState,
} from "./panels/RelationshipFormPane";
import { OwnershipToolbar } from "./OwnershipToolbar";
import {
    buildOwnershipGraph,
    bundleReferenceEdges,
    getVisibleLayoutedNodes,
} from "./ownershipGraph.helpers";
import {
    OWNERSHIP_UPDATE_TITLE,
    getOwnershipRelationshipDeleteBlockMessage,
} from "./ownershipProposal.helpers";
import { useElkLayout } from "./useElkLayout";
import { useCanvasNodePositions } from "./useCanvasNodePositions";
import {
    applySavedPositions,
    nudgeUnsavedNodes,
    unsavedIdsOf,
} from "./ownershipCanvasLayout.helpers";
import { useOwnershipMutations } from "./useOwnershipMutations";
import { useOwnershipRelationshipCreate } from "./useOwnershipRelationshipCreate";
import {
    expandGroupNodesForEdgeContent,
    labelAnchorForPoints,
    resolveLabelAnchors,
    routeOwnershipBus,
    routeReferenceEdges,
} from "./ownershipEdgeRouting.helpers";
import {
    ALL_RELATIONSHIPS,
    CANVAS_GRID,
    OwnershipEntityKindFilter,
    OwnershipFlowEdge,
    OwnershipFlowNode,
    OwnershipSelection,
    RelationshipFilter,
} from "./ownership.types";
import styles from "./OwnershipPage.module.scss";

// Distance an arrow-key press moves the selected node — one grid step.
const NODE_NUDGE_STEP = CANVAS_GRID;

interface DraftRelationshipEndpoints {
    sourceEntityUuid: string | null;
    targetEntityUuid: string | null;
}

export const OwnershipPage: React.FC = () => {
    const navigate = useStateAwareNavigate();
    const startAddEntity = useStartAddEntity();
    const workspace = useActiveWorkspace();
    const entities = useEntities();
    const { openOverlay } = useOverlays();
    const { error: toastError } = useToaster();
    const relationshipsQuery = useConsolidationRelationships(
        workspace?.id ?? null,
    );
    const groupsQuery = useConsolidationGroups(workspace?.id ?? null);

    const [selection, setSelection] = useState<OwnershipSelection>(null);
    const [formState, setFormState] = useState<RelationshipFormState | null>(
        null,
    );
    const [formError, setFormError] = useState<string | null>(null);
    const [draftRelationshipEndpoints, setDraftRelationshipEndpoints] =
        useState<DraftRelationshipEndpoints | null>(null);
    const [deleteRelationshipId, setDeleteRelationshipId] = useState<
        string | null
    >(null);
    const [dissolveGroupId, setDissolveGroupId] = useState<string | null>(null);
    const [isResetLayoutConfirmOpen, setIsResetLayoutConfirmOpen] =
        useState(false);
    const [search, setSearch] = useState("");
    const [relationshipTypeFilter, setRelationshipTypeFilter] =
        useState<RelationshipFilter>(ALL_RELATIONSHIPS);
    const [entityKindFilter, setEntityKindFilter] =
        useState<OwnershipEntityKindFilter>("all");

    const relationships = useMemo(
        () => relationshipsQuery.data ?? [],
        [relationshipsQuery.data],
    );
    const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);

    const entitiesByUuid = useMemo(
        () => new Map(entities.map((entity) => [entity.uuid, entity] as const)),
        [entities],
    );

    const handleSelectionChange = useCallback(
        (newSelection: OwnershipSelection) => {
            setSelection(newSelection);
            setFormState(null);
            setDraftRelationshipEndpoints(null);
        },
        [],
    );

    const closeForm = useCallback(() => {
        setFormState(null);
        setFormError(null);
        setDraftRelationshipEndpoints(null);
    }, []);
    const handleRelationshipDeleted = useCallback(() => {
        setDeleteRelationshipId(null);
        setSelection(null);
        setFormState(null);
    }, []);
    const closeDeleteModal = useCallback(
        () => setDeleteRelationshipId(null),
        [],
    );
    const closeDissolveModal = useCallback(() => {
        setDissolveGroupId(null);
        setSelection(null);
    }, []);

    const {
        createRelationshipMutation,
        updateRelationshipMutation,
        deleteRelationshipMutation,
        updateGroupConfigMutation,
        dissolveGroupMutation,
    } = useOwnershipMutations({
        workspaceId: workspace?.id ?? "",
        onRelationshipSaved: closeForm,
        onRelationshipSaveError: setFormError,
        onRelationshipDeleted: handleRelationshipDeleted,
        onDeleteSettled: closeDeleteModal,
        onDissolveSettled: closeDissolveModal,
    });

    // Stable identity so RelationshipFormPane's published submit action (and the
    // form's handleSubmit) don't change every render — an inline arrow here fed
    // the pane's effect and looped the page.
    const updateRelationship = updateRelationshipMutation.mutate;
    const handleUpdateRelationship = useCallback(
        (id: string, body: UpdateConsolidationRelationshipBody) =>
            updateRelationship({ id, body }),
        [updateRelationship],
    );

    const {
        handleCreate,
        pendingUpdate,
        clearPendingUpdate,
        confirmPendingUpdate,
        isReplacing,
    } = useOwnershipRelationshipCreate({
        workspaceId: workspace?.id ?? "",
        relationships,
        groups,
        entitiesByUuid,
        createRelationship: (body) => createRelationshipMutation.mutate(body),
        onSaved: closeForm,
        onError: setFormError,
    });

    const handleCloseForm = useCallback(() => {
        clearPendingUpdate();
        closeForm();
    }, [clearPendingUpdate, closeForm]);

    const handleEditRelationship = useCallback(
        (relationship: ConsolidationRelationshipResponse) => {
            setFormError(null);
            setFormState({ mode: "edit", relationship });
        },
        [],
    );

    const handleDeleteRelationship = useCallback(
        (relationshipId: string) => {
            const relationship = relationships.find(
                ({ id }) => id === relationshipId,
            );
            if (!relationship) {
                return;
            }
            const blockMessage = getOwnershipRelationshipDeleteBlockMessage({
                relationship,
                groups,
            });
            if (blockMessage !== null) {
                toastError(blockMessage);
                return;
            }
            setDeleteRelationshipId(relationshipId);
        },
        [groups, relationships, toastError],
    );

    const openCreateForm = useCallback(() => {
        setFormError(null);
        const sourceEntityUuid =
            selection?.kind === "entity" ? selection.entityUuid : null;
        setFormState({
            mode: "create",
            presetSourceEntityUuid: sourceEntityUuid ?? undefined,
        });
        setDraftRelationshipEndpoints({
            sourceEntityUuid,
            targetEntityUuid: null,
        });
    }, [selection]);

    // Dragging from one node's handle to another opens the create form with
    // both ends filled in; type and percentage are still chosen in the pane.
    const handleConnect = useCallback((connection: Connection) => {
        if (!connection.source || !connection.target) {
            return;
        }
        setFormError(null);
        setFormState({
            mode: "create",
            presetSourceEntityUuid: connection.source,
            presetTargetEntityUuid: connection.target,
        });
        setDraftRelationshipEndpoints({
            sourceEntityUuid: connection.source,
            targetEntityUuid: connection.target,
        });
    }, []);

    // Mirror the create-relationship rules: no self-edge, no group / group
    // header endpoints, and the target must be a business entity (the backend
    // rejects a personal target).
    const isValidConnection = useCallback<IsValidConnection>(
        (connection) => {
            const { source, target } = connection;
            if (!source || !target || source === target) {
                return false;
            }
            if (source.startsWith("group:") || target.startsWith("group:")) {
                return false;
            }
            return entitiesByUuid.get(target)?.isBusiness === true;
        },
        [entitiesByUuid],
    );

    const graph = useMemo(
        () =>
            buildOwnershipGraph({
                entities,
                relationships,
                groups,
                search,
                relationshipType:
                    relationshipTypeFilter === ALL_RELATIONSHIPS
                        ? undefined
                        : relationshipTypeFilter,
                entityKind: entityKindFilter,
            }),
        [
            entities,
            relationships,
            groups,
            search,
            relationshipTypeFilter,
            entityKindFilter,
        ],
    );

    const layoutGraph = useMemo(
        () =>
            buildOwnershipGraph({
                entities,
                relationships,
                groups,
                entityKind: entityKindFilter,
            }),
        [entities, relationships, groups, entityKindFilter],
    );

    const { nodes: layoutedNodes, isLayingOut } = useElkLayout(
        layoutGraph.nodes,
        layoutGraph.edges,
    );

    const { saved, isLoadingLayout, isFrozen, commitDrags, resetLayout } =
        useCanvasNodePositions(workspace?.id ?? "");

    // Consolidating or unconsolidating reparents nodes and adds/removes a group
    // box, so any saved manual layout is stale — drop it back to auto whenever
    // the set of consolidation groups changes (not on initial load or member
    // edits, which keep the same group ids).
    const groupIdsKey = useMemo(
        () =>
            groups
                .map((group) => group.id)
                .sort((a, b) => a.localeCompare(b))
                .join(","),
        [groups],
    );
    const prevGroupIdsKeyRef = useRef<{
        workspaceId: string | null;
        key: string;
    } | null>(null);
    useEffect(() => {
        if (groupsQuery.isLoading) {
            return;
        }
        const workspaceId = workspace?.id ?? null;
        const previous = prevGroupIdsKeyRef.current;
        prevGroupIdsKeyRef.current = { workspaceId, key: groupIdsKey };
        // Re-baseline (never reset) when the active workspace changes — the new
        // group set belongs to a different workspace, not a consolidation edit.
        if (
            previous !== null &&
            previous.workspaceId === workspaceId &&
            previous.key !== groupIdsKey &&
            isFrozen
        ) {
            resetLayout();
        }
    }, [
        workspace?.id,
        groupIdsKey,
        groupsQuery.isLoading,
        isFrozen,
        resetLayout,
    ]);

    // Substitute any hand-placed positions into ELK's output before routing and
    // group expansion, so both recompute against the arrangement the user sees.
    // Nodes with no saved position (newly added) keep their ELK slot, nudged
    // clear of the saved ones.
    const positionedNodes = useMemo(
        () =>
            nudgeUnsavedNodes({
                nodes: applySavedPositions({ layoutedNodes, saved }),
                unsavedIds: unsavedIdsOf(layoutedNodes, saved),
            }),
        [layoutedNodes, saved],
    );

    // Live drag positions (React Flow space) applied on top of the base layout
    // so routing and the edges follow the node during the gesture, not only on
    // drop. Persistence still references the pre-drag base (see the snapshot in
    // handleNodesDragStop), so this never corrupts the saved coordinates.
    const [liveDrag, setLiveDrag] = useState<Map<string, XYPosition> | null>(
        null,
    );
    const dragStartRenderedRef = useRef<OwnershipFlowNode[]>([]);

    const draggedNodes = useMemo(
        () =>
            liveDrag === null
                ? positionedNodes
                : positionedNodes.map((node) => {
                      const position = liveDrag.get(node.id);
                      return position ? { ...node, position } : node;
                  }),
        [positionedNodes, liveDrag],
    );

    const visibleLayoutedNodes = useMemo(
        () =>
            getVisibleLayoutedNodes({
                visibleNodes: graph.nodes,
                layoutedNodes: draggedNodes,
            }),
        [graph.nodes, draggedNodes],
    );

    // ELK lays out the nodes and routes ONLY the ownership tree (so reference
    // relationships can't deepen the hierarchy or stretch it). Same-direction
    // reference relationships are collapsed into one bundle, then routed
    // directly around the nearest side, with every label anchored on its edge.
    const { routedEdges, layoutedNodesForCanvas } = useMemo(() => {
        if (visibleLayoutedNodes.length === 0) {
            return { routedEdges: [], layoutedNodesForCanvas: [] };
        }
        const layoutedNodeIds = new Set(
            visibleLayoutedNodes.map(({ id }) => id),
        );
        const visibleEdges = bundleReferenceEdges(
            graph.edges.filter(
                ({ source, target }) =>
                    layoutedNodeIds.has(source) && layoutedNodeIds.has(target),
            ),
        );
        const referencePoints = routeReferenceEdges({
            nodes: visibleLayoutedNodes,
            edges: visibleEdges,
        });
        // Ownership edges route as a direction-aware org-chart bus (one trunk +
        // shared rail per side of each parent) from the current node rects, so
        // they stay correct after a manual drag and emanate from a single point
        // wherever a child is placed. Each route carries its own label anchor,
        // sat just outside the child.
        const ownershipRoutes = routeOwnershipBus({
            nodes: visibleLayoutedNodes,
            edges: visibleEdges,
        });
        const pointsByEdgeId = new Map(
            visibleEdges.flatMap((edge) => {
                const route = ownershipRoutes.get(edge.id);
                if (route) {
                    return [[edge.id, route.points]] as const;
                }
                const routed = referencePoints.get(edge.id);
                return routed ? ([[edge.id, routed]] as const) : [];
            }),
        );
        const anchorsByEdgeId = resolveLabelAnchors({
            anchorsByEdgeId: new Map(
                [...pointsByEdgeId].map(([edgeId, points]) => {
                    const route = ownershipRoutes.get(edgeId);
                    return [
                        edgeId,
                        route ? route.label : labelAnchorForPoints(points),
                    ];
                }),
            ),
            pointsByEdgeId,
            nodes: visibleLayoutedNodes,
        });
        const routedEdgesResult = visibleEdges.flatMap((edge) => {
            const points = pointsByEdgeId.get(edge.id);
            if (!points || !edge.data) {
                return [];
            }
            return [
                {
                    ...edge,
                    data: {
                        ...edge.data,
                        points,
                        labelAnchor: anchorsByEdgeId.get(edge.id),
                    },
                },
            ];
        });
        return {
            routedEdges: routedEdgesResult,
            // Grow each consolidation boundary to contain its intra-group
            // relationships (edges + labels), which loop around the side.
            layoutedNodesForCanvas: expandGroupNodesForEdgeContent({
                nodes: visibleLayoutedNodes,
                edges: routedEdgesResult,
            }),
        };
    }, [graph.edges, visibleLayoutedNodes]);

    const canvasEdges = useMemo((): OwnershipFlowEdge[] => {
        if (formState?.mode !== "create" || !draftRelationshipEndpoints) {
            return routedEdges;
        }
        const { sourceEntityUuid, targetEntityUuid } =
            draftRelationshipEndpoints;
        if (
            !sourceEntityUuid ||
            !targetEntityUuid ||
            sourceEntityUuid === targetEntityUuid
        ) {
            return routedEdges;
        }
        const visibleNodeIds = new Set(
            layoutedNodesForCanvas.map(({ id }) => id),
        );
        if (
            !visibleNodeIds.has(sourceEntityUuid) ||
            !visibleNodeIds.has(targetEntityUuid)
        ) {
            return routedEdges;
        }

        return [
            ...routedEdges,
            {
                id: `draft:${sourceEntityUuid}:${targetEntityUuid}`,
                type: "ownershipEdge",
                source: sourceEntityUuid,
                target: targetEntityUuid,
                data: {
                    isDraft: true,
                    relationship: {
                        id: "00000000-0000-0000-0000-000000000000",
                        workspaceId: workspace?.id ?? "",
                        type: ConsolidationEntityRelationshipType.ownership,
                        sourceEntityUuid,
                        targetEntityUuid,
                        ownershipPercentage: 100,
                        startDate: null,
                        endDate: null,
                        metadata: null,
                    },
                },
            },
        ];
    }, [
        draftRelationshipEndpoints,
        formState?.mode,
        layoutedNodesForCanvas,
        routedEdges,
        workspace?.id,
    ]);

    const canvasNodes = useMemo(() => {
        const headerNodes = layoutedNodesForCanvas.flatMap(
            (node): OwnershipFlowNode[] =>
                node.type === "groupNode"
                    ? [
                          {
                              id: `${node.id}:header`,
                              type: "groupHeaderNode",
                              position: {
                                  x: node.position.x,
                                  y: node.position.y,
                              },
                              width: node.width,
                              height: 0,
                              zIndex: 1000,
                              selectable: false,
                              draggable: false,
                              focusable: false,
                              style: { pointerEvents: "none" },
                              data: {
                                  group: node.data.group,
                                  isSelected:
                                      selection?.kind === "group" &&
                                      selection.groupId === node.data.group.id,
                                  onSelect: () =>
                                      handleSelectionChange({
                                          kind: "group",
                                          groupId: node.data.group.id,
                                      }),
                              },
                          },
                      ]
                    : [],
        );
        return [...layoutedNodesForCanvas, ...headerNodes];
    }, [layoutedNodesForCanvas, selection, handleSelectionChange]);

    // Snapshot the pre-drag rendered (post-expansion) positions so the commit
    // can measure the drag delta against them — the live-drag render mutates
    // `layoutedNodesForCanvas` during the gesture, so it can't be the reference.
    const handleNodesDragStart = useCallback(() => {
        dragStartRenderedRef.current = layoutedNodesForCanvas;
    }, [layoutedNodesForCanvas]);

    const handleNodesDrag = useCallback(
        (moves: ReadonlyArray<{ id: string; position: XYPosition }>) => {
            setLiveDrag(
                new Map(moves.map(({ id, position }) => [id, position])),
            );
        },
        [],
    );

    const handleNodesDragStop = useCallback(
        (
            drags: ReadonlyArray<{
                id: string;
                position: { x: number; y: number };
            }>,
        ) => {
            commitDrags({
                positionedNodes,
                renderedNodes: dragStartRenderedRef.current,
                drags,
            });
            setLiveDrag(null);
        },
        [commitDrags, positionedNodes],
    );

    useKeyboardCommand({
        keys: ["up", "down", "left", "right"],
        callback: (event) => {
            if (selection?.kind !== "entity") {
                return;
            }
            const node = layoutedNodesForCanvas.find(
                ({ id }) => id === selection.entityUuid,
            );
            if (!node) {
                return;
            }
            const deltaByKey: Record<string, [number, number]> = {
                ArrowUp: [0, -NODE_NUDGE_STEP],
                ArrowDown: [0, NODE_NUDGE_STEP],
                ArrowLeft: [-NODE_NUDGE_STEP, 0],
                ArrowRight: [NODE_NUDGE_STEP, 0],
            };
            const delta = deltaByKey[event.key];
            if (!delta) {
                return;
            }
            commitDrags({
                positionedNodes,
                renderedNodes: layoutedNodesForCanvas,
                drags: [
                    {
                        id: node.id,
                        position: {
                            x: node.position.x + delta[0],
                            y: node.position.y + delta[1],
                        },
                    },
                ],
            });
        },
        options: {
            description: "Nudge the selected entity on the ownership canvas",
            preventDefault: true,
        },
        dependencies: [
            selection,
            layoutedNodesForCanvas,
            positionedNodes,
            commitDrags,
        ],
    });

    useKeyboardCommand({
        keys: ["delete", "backspace"],
        callback: () => {
            if (selection?.kind === "relationship") {
                setDeleteRelationshipId(selection.relationshipId);
            }
        },
        options: {
            description: "Delete the selected relationship",
            preventDefault: true,
        },
        dependencies: [selection],
    });

    const isLoading = relationshipsQuery.isLoading || groupsQuery.isLoading;
    const realEntities = useMemo(
        () => entities.filter((entity) => !entity.isMockEntity),
        [entities],
    );

    // Mutations must never fire with an empty workspace id.
    if (isLoading || !workspace) {
        return (
            <div className={styles.centered}>
                <Loader />
            </div>
        );
    }

    if (realEntities.length < 2) {
        return (
            <div className={styles.centered}>
                <EmptyState
                    header="Ownership structure needs at least two entities"
                    body="Add another business or owner entity to start mapping ownership and relationships."
                    ctaText="Add entity"
                    action={() => startAddEntity()}
                />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <OwnershipToolbar
                search={search}
                onSearchChange={setSearch}
                relationshipTypeFilter={relationshipTypeFilter}
                onRelationshipTypeFilterChange={setRelationshipTypeFilter}
                entityKindFilter={entityKindFilter}
                onEntityKindFilterChange={setEntityKindFilter}
                hasConsolidationGroups={groups.length > 0}
                onAddConsolidationGroup={() =>
                    openOverlay("consolidationWorkflow", {})
                }
                onAddEntity={() => startAddEntity({ returnTo: "/ownership" })}
            />
            <div className={styles.content}>
                <div className={styles.main}>
                    <ReactFlowProvider>
                        <OwnershipCanvas
                            nodes={canvasNodes}
                            edges={canvasEdges}
                            isLoading={isLayingOut || isLoadingLayout}
                            selection={selection}
                            onSelectionChange={handleSelectionChange}
                            onNodesDragStart={handleNodesDragStart}
                            onNodesDrag={handleNodesDrag}
                            onNodesDragStop={handleNodesDragStop}
                            onConnect={handleConnect}
                            isValidConnection={isValidConnection}
                        />
                        <OwnershipCommandBar
                            selection={formState ? null : selection}
                            entitiesByUuid={entitiesByUuid}
                            relationships={relationships}
                            groups={groups}
                            isLayoutFrozen={isFrozen}
                            onResetLayout={() =>
                                setIsResetLayoutConfirmOpen(true)
                            }
                            onAddRelationship={openCreateForm}
                            onNavigate={navigate}
                            onConsolidate={({
                                parentEntityUuid,
                                memberEntityUuids,
                            }) =>
                                openOverlay("consolidationWorkflow", {
                                    initialSelection: {
                                        parentEntityUuid,
                                        memberEntityUuids,
                                    },
                                })
                            }
                            onUnconsolidate={setDissolveGroupId}
                            onEditRelationship={handleEditRelationship}
                            onDeleteRelationship={handleDeleteRelationship}
                        />
                    </ReactFlowProvider>
                </div>
                {formState ? (
                    <RelationshipFormPane
                        key={`${formState.mode}-${formState.relationship?.id ?? "new"}-${formState.presetSourceEntityUuid ?? ""}-${formState.presetTargetEntityUuid ?? ""}`}
                        formState={formState}
                        entities={realEntities}
                        onClose={handleCloseForm}
                        onCreate={handleCreate}
                        onUpdate={handleUpdateRelationship}
                        onDraftEndpointsChange={setDraftRelationshipEndpoints}
                        isSaving={
                            createRelationshipMutation.isPending ||
                            updateRelationshipMutation.isPending ||
                            isReplacing
                        }
                        errorMessage={formError}
                    />
                ) : (
                    <OwnershipSidePanel
                        workspaceId={workspace.id}
                        selection={selection}
                        entitiesByUuid={entitiesByUuid}
                        relationships={relationships}
                        groups={groups}
                        onSelectionChange={handleSelectionChange}
                        onEditRelationship={handleEditRelationship}
                        onDeleteRelationship={handleDeleteRelationship}
                        isUpdatingGroupConfig={
                            updateGroupConfigMutation.isPending
                        }
                        onUpdateGroupConfig={(
                            { groupId, ledgerIdsByEntityUuid },
                            callbacks,
                        ) =>
                            updateGroupConfigMutation.mutate(
                                {
                                    groupId,
                                    body: { ledgerIdsByEntityUuid },
                                },
                                { onError: callbacks?.onError },
                            )
                        }
                    />
                )}
            </div>
            <ConfirmationModal
                show={pendingUpdate !== null}
                title={OWNERSHIP_UPDATE_TITLE}
                question={pendingUpdate?.confirmationBody}
                yes="Update"
                no="Cancel"
                onHide={clearPendingUpdate}
                onReject={clearPendingUpdate}
                onConfirm={confirmPendingUpdate}
            />
            <ConfirmationModal
                show={deleteRelationshipId !== null}
                title="Delete relationship"
                question="This removes the relationship from the ownership canvas. This cannot be undone."
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onHide={() => setDeleteRelationshipId(null)}
                onConfirm={() => {
                    if (deleteRelationshipId) {
                        deleteRelationshipMutation.mutate(deleteRelationshipId);
                    }
                }}
            />
            <ConfirmationModal
                show={dissolveGroupId !== null}
                title="Unconsolidate group"
                question="This dissolves the consolidation group. Entities and relationships stay on the canvas and the group can be recreated later."
                yes="Unconsolidate"
                yesBtnVariant="destructive"
                no="Cancel"
                onHide={() => setDissolveGroupId(null)}
                onConfirm={() => {
                    if (dissolveGroupId) {
                        dissolveGroupMutation.mutate(dissolveGroupId);
                    }
                }}
            />
            <ConfirmationModal
                show={isResetLayoutConfirmOpen}
                title="Restore automatic layout"
                question="This updates the canvas arrangement for everyone in the workspace. Entity relationships stay unchanged."
                yes="Restore layout"
                no="Cancel"
                onHide={() => setIsResetLayoutConfirmOpen(false)}
                onConfirm={() => {
                    resetLayout();
                    setIsResetLayoutConfirmOpen(false);
                }}
            />
        </div>
    );
};
